import { z } from "zod";

/**
 * Stored shapes for the karaoke composition: text styling and layout.
 *
 * These live in `convex/` because they describe what the backend persists —
 * `convex/schema.ts` derives its validators from them, and the frontend
 * consumes them through `@/types/types`, which re-exports this module. The
 * dependency runs one way, src -> convex; nothing under `convex/` may import
 * from `src/`.
 *
 * `LyricsSchema` and the `Overlay` union are the same kind of thing and belong
 * here too, but they are moved by the phase that migrates their column (P5),
 * not before.
 */

const TextCaseSchema = z
  // "capitalize" is legacy: 6 production presets carry it (measured on the
  // clone 2026-09-18) and it is no longer offered in the editor. Declared
  // rather than dropped so the migration imports those presets unchanged —
  // `.catch("none")` would otherwise silently restyle someone's saved work.
  .union([
    z.literal("none"),
    z.literal("uppercase"),
    z.literal("lowercase"),
    z.literal("capitalize"),
  ])
  .catch("none")
  .default("none");

export type TextCase = z.infer<typeof TextCaseSchema>;

// Define the Zod schema for TextStyle
export const TextStyleSchema = z.object({
  textColor: z.string().default("#ffffff"),
  wipeColor: z.string().default("#fab005"),
  outlineColor: z.string().default("#000000"),
  fontName: z.string().default("Noto Sans"),
  // Writing system (Google Fonts script subset, e.g. "latin", "korean") the
  // font was picked for. Persists the FontPicker's selected script so it
  // reopens to the right writing system instead of resetting to Latin.
  writingSystem: z.string().default("latin"),
  fontSize: z.number().default(72),
  outlineWidth: z.number().default(10),
  letterSpacing: z.number().default(0),
  lineSpacing: z.number().default(1.5),
  textCase: TextCaseSchema,
  direction: z
    .union([z.literal("ltr"), z.literal("rtl")])
    .catch("ltr")
    .default("ltr"),
  fontWeight: z.number().default(900),
  backgroundColor: z.string().optional().default("rgba(0, 0, 0, 0)"),
  textEffect: z
    .discriminatedUnion("type", [
      z.object({ type: z.literal("none") }),
      z.object({ type: z.literal("karaoke") }),
      z.object({
        type: z.literal("highlight"),
        sungColor: z.string().optional(),
        bgColor: z.string().optional(),
      }),
    ])
    .catch({ type: "karaoke" })
    .default({ type: "karaoke" }),
});

// Translation types and schemas
const TranslationLineSchema = z.object({
  id: z.number(),
  text: z.string(),
});

export const TranslationDataSchema = z.object({
  lines: z.array(TranslationLineSchema),
});

export type TranslationData = z.infer<typeof TranslationDataSchema>;

// Layout types and schemas
export const VerticalPositionSchema = z
  .union([z.literal("start"), z.literal("center"), z.literal("end")])
  .catch("center")
  .default("center");
export type VerticalPosition = z.infer<typeof VerticalPositionSchema>;

const LinesPerScreenSchema = z
  .union([z.literal(2), z.literal(4), z.literal(6), z.literal(8)])
  .catch(4)
  .default(4);
export type LinesPerScreen = z.infer<typeof LinesPerScreenSchema>;

export const DefaultLayoutSchema = z.object({
  type: z.literal("default"),
  linesPerScreen: LinesPerScreenSchema,
  horizontalMargin: z.number().min(0).max(20).default(3),
  verticalMargin: z.number().min(0).max(20).default(0),
  isDuetMode: z.boolean().default(false),
});
export type DefaultLayout = z.infer<typeof DefaultLayoutSchema>;

const RowHeightSchema = z.enum(["compact", "normal", "relaxed"]);

export const KaraokeLayoutSchema = z.object({
  type: z.literal("karaoke"),
  linesPerScreen: z.number().int().min(2).max(8).default(4),
  verticalPosition: VerticalPositionSchema,
  allowTextWrapping: z.boolean().default(true),
  rowHeight: RowHeightSchema.catch("normal").default("normal"),
  horizontalMargin: z.number().min(0).max(20).default(2),
  verticalMargin: z.number().min(0).max(20).default(2),
  heightPercentage: z.number().min(20).max(100).default(100),
  isDuetMode: z.boolean().default(false),
});
export type KaraokeLayout = z.infer<typeof KaraokeLayoutSchema>;

const SingleLayoutAnimationSchema = z.union([
  z.literal("none"),
  z.literal("slide"),
  z.literal("zoom"),
  z.literal("shake"),
  z.literal("fade"),
]);

export type SingleLayoutAnimation = z.infer<typeof SingleLayoutAnimationSchema>;

export const SingleLayoutSchema = z.object({
  type: z.literal("single"),
  animation: SingleLayoutAnimationSchema.catch("slide").default("slide"),
  verticalPosition: VerticalPositionSchema,
  horizontalMargin: z.number().min(0).max(20).default(5),
  verticalMargin: z.number().min(0).max(20).default(5),
  isDuetMode: z.boolean().default(false),
});
export type SingleLayout = z.infer<typeof SingleLayoutSchema>;

export const DuetLayoutSchema = z.object({
  type: z.literal("duet"),
  bothSingers: z.boolean().default(false),
  horizontalMargin: z.number().min(0).max(20).default(3),
  verticalMargin: z.number().min(0).max(20).default(0),
  isDuetMode: z.boolean().default(true),
});
export type DuetLayout = z.infer<typeof DuetLayoutSchema>;

const DualLanguageLinesPerScreenSchema = z
  .union([z.literal(1), z.literal(2)])
  .catch(1)
  .default(1);

export const DualLanguageLayoutSchema = z.object({
  type: z.literal("dual-language"),
  linesPerScreen: DualLanguageLinesPerScreenSchema,
  verticalPosition: VerticalPositionSchema.default("center"),
  translationPosition: z
    .enum(["top", "bottom"])
    .catch("bottom")
    .default("bottom"),
  originalAnimation:
    SingleLayoutAnimationSchema.catch("slide").default("slide"),
  translationAnimation:
    SingleLayoutAnimationSchema.catch("slide").default("slide"),
  horizontalMargin: z.number().min(0).max(20).default(3),
  verticalMargin: z.number().min(0).max(20).default(0),
  // Distance between the original and translation, as a % of video height
  languageGap: z.number().min(0).max(50).default(1),
  isDuetMode: z.boolean().default(false),
  languageCode: z.string().default(""),
  translationStyle: TextStyleSchema.default({
    ...TextStyleSchema.parse({}), // Get all defaults from TextStyleSchema
    textEffect: { type: "none" }, // Override textEffect for translations
  }),
});
export type DualLanguageLayout = z.infer<typeof DualLanguageLayoutSchema>;

export const ScrollLayoutSchema = z.object({
  type: z.literal("scroll"),
  zoom: z.boolean().default(false),
  glow: z.boolean().default(false),
  glowColor: z.string().optional(),
  hideDuringBreaks: z.boolean().default(false),
  verticalPosition: z
    .union([z.literal("start"), z.literal("center"), z.literal("end")])
    .catch("center")
    .default("center"),
  height: z
    .union([z.literal("narrow"), z.literal("medium"), z.literal("full")])
    .catch("full")
    .default("full"),
  horizontalMargin: z.number().min(0).max(20).default(0),
  verticalMargin: z.number().min(0).max(20).default(0),
  isDuetMode: z.boolean().default(false),
});
export type ScrollLayout = z.infer<typeof ScrollLayoutSchema>;

export const LayoutSchema = z
  .discriminatedUnion("type", [
    DefaultLayoutSchema,
    KaraokeLayoutSchema,
    SingleLayoutSchema,
    DuetLayoutSchema,
    DualLanguageLayoutSchema,
    ScrollLayoutSchema,
  ])
  .catch(() =>
    DefaultLayoutSchema.parse({ type: "default", linesPerScreen: 4 }),
  )
  .nullish()
  .transform(
    (val) =>
      val ?? DefaultLayoutSchema.parse({ type: "default", linesPerScreen: 4 }),
  );

export type Layout = z.infer<typeof LayoutSchema>;
